import { isValidElement } from 'react';
import {
  TAB_SIZE_TOKENS,
  tabRootClass,
  tabToneClasses,
} from './tabClasses.js';
import { Info } from 'lucide-react';

function renderInfoSlot(info, iconSize) {
  if (info === true) {
    return <Info size={iconSize} className="shrink-0 text-current" strokeWidth={2} aria-hidden />;
  }

  if (info == null || info === false) {
    return null;
  }

  if (isValidElement(info)) {
    return info;
  }

  return <span className="shrink-0 text-current">{info}</span>;
}

/**
 * Tab item for {@link TabNav} — Figma Design System AIMS OS (node 856:11310).
 *
 * Supports selected/unselected, default/hover/disabled, sizes M/S, optional leading icon,
 * and optional info badge (boolean shows info icon; pass a node for counts/alerts).
 *
 * @example
 * <TabNav ariaLabel="Section tabs">
 *   <Tab selected icon={User} info>Users</Tab>
 *   <Tab icon={User}>Roles</Tab>
 * </TabNav>
 *
 * @param {object} props
 * @param {boolean} [props.selected=false]
 * @param {boolean} [props.disabled=false]
 * @param {'md' | 'sm'} [props.size='md']
 * @param {import('lucide-react').LucideIcon} [props.icon]
 * @param {boolean | import('react').ReactNode} [props.info]
 * @param {string} [props.className]
 * @param {string} [props.ariaControls]
 * @param {string} [props.ariaLabel]
 * @param {(event: import('react').MouseEvent<HTMLButtonElement>) => void} [props.onClick]
 * @param {import('react').ReactNode} props.children
 */
const Tab = ({
  selected = false,
  disabled = false,
  size = 'md',
  icon: IconComp,
  info,
  className = '',
  ariaControls,
  ariaLabel,
  onClick,
  children,
  type = 'button',
  ...rest
}) => {
  const sizeKey = size === 'sm' ? 'sm' : 'md';
  const sizeTokens = TAB_SIZE_TOKENS[sizeKey];
  const iconSize = sizeTokens.iconSize;

  return (
    <button
      type={type}
      role="tab"
      aria-selected={selected}
      aria-disabled={disabled || undefined}
      aria-controls={ariaControls}
      aria-label={ariaLabel}
      disabled={disabled}
      tabIndex={selected ? 0 : -1}
      onClick={onClick}
      className={[
        tabRootClass,
        sizeTokens.container,
        tabToneClasses({ selected, disabled }),
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      {...rest}
    >
      {IconComp && (
        <IconComp size={iconSize} className="shrink-0 text-current" strokeWidth={2} aria-hidden />
      )}
      <span>{children}</span>
      {renderInfoSlot(info, iconSize)}
    </button>
  );
};

export default Tab;
